import { Body, Box, Button, Icon } from '@hover/blueprint';
import { iDownload } from '@hover/icons';
import React, { useState } from 'react';

import { useRenderingContext } from 'src/modules/renderings/hooks';
import type { ImageLike } from 'src/modules/renderings/imageLike';
import { FeatureTracking } from 'src/shared/constants/FeatureFlag';
import { useProfileQuery } from 'src/shared/graphql/generated';
import { TranslationNamespace, useTranslate } from 'src/shared/hooks';
import { Page, buttonPressed } from 'src/shared/telemetry';

import { saveToZip } from '../DownloadImage';

import { ImageThumbnailContainer } from './ImageThumbnailContainer';

interface IAccordionItemContentProps {
  heroImageURL?: string;
  images: ImageLike[];
  handleImageSelect: Function;
  title: string;
  hideDownloadButton?: boolean;
  initialItemMax?: number;
  versionId?: string;
  downloadFileName: string;
}

const AccordionItemContent = ({
  heroImageURL,
  images,
  handleImageSelect,
  title,
  versionId,
  hideDownloadButton,
  initialItemMax,
  downloadFileName,
}: IAccordionItemContentProps) => {
  const { jobId, orgId } = useRenderingContext();
  const { data: profileData } = useProfileQuery({
    context: { skipRedirect: true },
  });
  const userId = profileData?.profile.user.id;
  const [shouldShowAll, setShouldShowAll] = useState(false);
  const t = useTranslate(TranslationNamespace.SHARED);

  const handleTracking =
    (buttonText: string, trackId: string, itemType?: string) => () => {
      const buttonPressedTrackingData = {
        button_text: buttonText,
        feature: FeatureTracking.RENDERING,
        job_id: jobId,
        org_id: orgId,
        user_id: userId,
        track_id: trackId,
        version_id: versionId,
        item_type: itemType,
        page_or_screen_name: Page.RENDERINGS_HUB,
        primary_cta: false,
      };

      buttonPressed(buttonPressedTrackingData);
    };

  const downloadSetText = 'Download set';
  const fullImageURLs = images.map(img => img.blobUrl);
  const handleDownloadSet = () => {
    handleTracking(downloadSetText, 'rendering-download-set');
    saveToZip(fullImageURLs, downloadFileName);
  };

  const shownImages =
    initialItemMax && !shouldShowAll ? images.slice(0, initialItemMax) : images;

  const shouldDisplayShowAllButton =
    initialItemMax && initialItemMax < images.length;

  return (
    <>
      <Box display="flex" flexDirection="column" marginTop={400}>
        <Box
          display="grid"
          gap={200}
          gridColumnGap="8px"
          gridRowGap="8px"
          gridTemplateColumns="1fr 1fr"
          key={title}
          pb={50}
          pl={50}
        >
          {shownImages.map((image, index) => (
            <ImageThumbnailContainer
              handleImageSelect={handleImageSelect}
              handleTracking={handleTracking(
                `${versionId ? `Image` : 'Original'} ${index}`,
                `${versionId ? `image` : 'original'}-${index}-thumbnail`,
                versionId,
              )}
              heroImageURL={heroImageURL}
              image={image}
              key={image.id}
            />
          ))}
        </Box>
        {shouldDisplayShowAllButton && (
          <Button
            fill="minimal"
            fontSize={200}
            fontWeight={400}
            label="view more"
            marginTop={400}
            onClick={() => setShouldShowAll(!shouldShowAll)}
          >
            {shouldShowAll
              ? t('renderings.viewLess')
              : t('renderings.viewMore')}
          </Button>
        )}
      </Box>
      <Box
        cursor="pointer"
        display={hideDownloadButton ? 'none' : 'flex'}
        justifyContent="center"
        mt={400}
        onClick={handleDownloadSet}
        testId="download-link"
      >
        <Box alignItems="center" justifyContent="center">
          <Body color="neutral.600" margin={0} size={400}>
            {downloadSetText}
          </Body>
          <Icon color="neutral.600" icon={iDownload} size="small" />
        </Box>
      </Box>
    </>
  );
};

export { AccordionItemContent };
